import { Typography } from '@/components/typography';
import { cn } from '@/lib/utils';
import { type ReactElement, type ReactNode } from 'react';

interface ProductInfoCardProps {
    /** Optional icon element displayed on the left side of the card */
    icon?: ReactNode;
    /** Card title text (rendered bold) */
    title?: string;
    /** Card description or subtitle text */
    description?: string;
    richText?: string;
    /** Optional action link/button rendered below the description */
    action?: {
        label: string;
        onClick: () => void;
    };
    /** Optional CSS class for the border (default: border with visible light gray) */
    borderClassName?: string;
    /** Optional additional CSS classes */
    className?: string;
}

/**
 * Reusable info card component for PDP sections such as Free Shipping,
 * Returns & Warranty, and Estimated Delivery.
 *
 * Renders a card with a full border, icon, title, description,
 * and optional action link. Designed for consistent PDP info cards.
 *
 * @example
 * ```tsx
 * <ProductInfoCard
 *     icon={<CalendarDays className="h-5 w-5" />}
 *     title="Estimated Delivery"
 *     description="Sep 15-16 · Shipping options available"
 *     action={{ label: "Learn More", onClick: handleLearnMore }}
 * />
 * ```
 */
export default function ProductInfoCard({
    icon,
    title,
    description,
    action,
    richText,
    borderClassName,
    className,
}: ProductInfoCardProps): ReactElement {
    return (
        <div
            className={cn(
                'flex items-start gap-3 rounded-sm bg-neutral-white p-4',
                borderClassName ?? 'border-order-border',
                className
            )}>
            {icon && <div className="flex-shrink-0 mt-0.5 text-muted-foreground">{icon}</div>}
            <div className="min-w-0 flex-1">
                {title && (
                    <Typography variant="small" className="body-copy-l-bold">
                        {title}
                    </Typography>
                )}
                {description && (
                    <Typography variant="muted" className="mt-1 body-copy-m">
                        {description}
                    </Typography>
                )}
                {/* eslint-disable-next-line react/no-danger */}
                {richText && <div className="mt-1" dangerouslySetInnerHTML={{ __html: richText }} />}
                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        aria-label={`${action.label} - ${title}`}
                        className="mt-1 cursor-pointer text-xs font-normal text-primary hover:underline">
                        {action.label}
                    </button>
                )}
            </div>
        </div>
    );
}
